import React, { useEffect, useRef, useState } from 'react';
import { Text } from '../../../../style-utils/text';
import { palette } from '../../../../style-utils/theme';
import { RightArrow } from '../../../icons/right-arrow';
import {
  ButtonsContainer,
  ButtonTitle,
  CancelButton,
  ErrorLabel,
  ErrorNotice,
  FormBody,
  FormHeader,
  HelperText,
  InputContainer,
  Label,
  ModalBackground,
  PortfolioFormCard,
  SaveButton,
  StyledPortfolioInput,
  StyledPortfolioTextarea,
} from './dashboard-portfolios.styled';
import {
  InlineMessage,
  StatusLabel,
  scrimMotion,
  useOverlayMotion,
} from '../../../../motion';
import {
  createPortfolio,
  HierarchyApiError,
  updatePortfolio,
} from '../../../../api/hierarchy-client';
import { Portfolio } from '../../../../interfaces/hierarchy-interfaces';
import { useDialogFocus } from '../../../hooks/use-dialog-focus';
import { ModalPortal } from '../../../modal/modal-portal';

interface DashboardPortfolioFormProps {
  // null => create a new portfolio; a row => edit that portfolio.
  portfolio: Portfolio | null;
  onCancel: () => void;
  onSaved: (portfolio: Portfolio) => void;
}

// The optional columns are nullable in Postgres, so an emptied field has to
// travel as null rather than '' — otherwise clearing a code would store a blank
// string that the unique/lookup logic treats as a real value.
const toNullable = (value: string): string | null => {
  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : null;
};

export const DashboardPortfolioForm: React.FC<DashboardPortfolioFormProps> = ({
  portfolio,
  onCancel,
  onSaved,
}) => {
  const isEdit = portfolio !== null;

  const [name, setName] = useState<string>(portfolio?.name ?? '');
  const [code, setCode] = useState<string>(portfolio?.code ?? '');
  const [description, setDescription] = useState<string>(
    portfolio?.description ?? ''
  );
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const savingRef = useRef(false);

  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const dialogRef = useDialogFocus<HTMLFormElement>(true, () => {
    if (!savingRef.current) {
      onCancel();
    }
  });

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (savingRef.current) {
      return;
    }

    const trimmedName = name.trim();

    if (trimmedName.length === 0) {
      setNameError('Please enter a portfolio name*');
      nameInputRef.current?.focus();

      return;
    }

    setNameError(null);
    setSubmitError(null);
    savingRef.current = true;
    setIsSaving(true);

    try {
      const payload = {
        name: trimmedName,
        code: toNullable(code),
        description: toNullable(description),
      };

      const saved =
        isEdit && portfolio
          ? await updatePortfolio(portfolio.id, payload)
          : await createPortfolio(payload);

      savingRef.current = false;
      onSaved(saved);
    } catch (error) {
      // The server's wording carries the detail a generic string cannot — the
      // name that collided, the field it rejected. Show it verbatim.
      const message =
        error instanceof HierarchyApiError
          ? error.message
          : 'Something went wrong. Please try again.';

      setSubmitError(message);
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  /* Centred modal, bottom sheet on a phone; rendered by the caller inside a
     keyed Presence so it animates out as well. */
  const modalMotion = useOverlayMotion('modal');

  return (
    <ModalPortal>
      <ModalBackground
        {...scrimMotion}
        id="portfolio-form-background"
        onMouseDown={() => {
          if (!savingRef.current) {
            onCancel();
          }
        }}
      >
        <PortfolioFormCard
          {...modalMotion}
          ref={dialogRef}
          tabIndex={-1}
          id="portfolio-form"
          onMouseDown={event => event.stopPropagation()}
          onSubmit={handleSubmit}
          role="dialog"
          aria-modal="true"
          aria-labelledby="portfolio-form-heading"
          noValidate
        >
          <FormHeader>
            <Text
              id="portfolio-form-heading"
              mt={0}
              mb={0}
              fontSize={20}
              fontWeight={700}
              color={palette.ink}
            >
              {isEdit ? 'Edit portfolio' : 'Add portfolio'}
            </Text>
            <Text mt={0} mb={0} fontSize={13} color={palette.slate}>
              {isEdit
                ? 'Update how this group of buildings is labelled.'
                : 'Group buildings together to manage access and reporting.'}
            </Text>
          </FormHeader>

          <FormBody>
            <ErrorNotice message={submitError} />

            <InputContainer>
              <Label htmlFor="portfolio-name">Portfolio name</Label>
              <StyledPortfolioInput
                id="portfolio-name"
                ref={nameInputRef}
                type="text"
                name="name"
                value={name}
                maxLength={120}
                isInvalid={nameError !== null}
                aria-invalid={nameError !== null}
                aria-describedby={nameError ? 'portfolio-name-error' : undefined}
                placeholder="Downtown Residences"
                onChange={event => {
                  setName(event.target.value);

                  if (nameError) {
                    setNameError(null);
                  }
                }}
              />
              {/* Unfolds under the field; the id aria-describedby points at
                  is on the message wrapper, which exists only while it shows. */}
              <InlineMessage
                id="portfolio-name-error"
                message={
                  nameError ? (
                    <ErrorLabel htmlFor="portfolio-name">{nameError}</ErrorLabel>
                  ) : undefined
                }
              />
            </InputContainer>

            <InputContainer>
              <Label htmlFor="portfolio-code">
                Code <HelperText>(optional)</HelperText>
              </Label>
              <StyledPortfolioInput
                id="portfolio-code"
                type="text"
                name="code"
                value={code}
                maxLength={40}
                placeholder="DTR-01"
                onChange={event => setCode(event.target.value)}
              />
              <HelperText>A short reference used in reports.</HelperText>
            </InputContainer>

            <InputContainer>
              <Label htmlFor="portfolio-description">
                Description <HelperText>(optional)</HelperText>
              </Label>
              <StyledPortfolioTextarea
                id="portfolio-description"
                name="description"
                value={description}
                maxLength={500}
                placeholder="What this portfolio covers."
                onChange={event => setDescription(event.target.value)}
              />
            </InputContainer>
          </FormBody>

          <ButtonsContainer>
            <CancelButton type="button" disabled={isSaving} onClick={onCancel}>
              <ButtonTitle>Cancel</ButtonTitle>
            </CancelButton>
            {/* The label swaps without the button changing width (the arrow
                used to drop out mid-save and the button shrank with it). */}
            <SaveButton type="submit" disabled={isSaving} aria-busy={isSaving}>
              <StatusLabel
                state={isSaving ? 'busy' : 'idle'}
                labels={{
                  idle: (
                    <>
                      <ButtonTitle>
                        {isEdit ? 'Save changes' : 'Add portfolio'}
                      </ButtonTitle>
                      <RightArrow />
                    </>
                  ),
                  busy: <ButtonTitle>Saving…</ButtonTitle>,
                }}
              />
            </SaveButton>
          </ButtonsContainer>
        </PortfolioFormCard>
      </ModalBackground>
    </ModalPortal>
  );
};
